// The supervisor app's update notice — one push that tells EVERY supervisor a
// newer build is live on the Play Store, and opens the store page when tapped.
//
// The app already shows Play's own update prompt when it is opened, so this is
// for the phones that keep it resident for days and are never asked. That is
// also why it is a deliberate, confirmed action rather than something the
// panel does on its own: it buzzes every supervisor at once and cannot be
// taken back.

import React, { useEffect, useState } from 'react';
import { FiAlertTriangle, FiSend, FiSmartphone, FiX } from 'react-icons/fi';
import { Alert, Button, Card } from '../common/index.js';
import { appUpdateApi } from '../../api/appUpdateApi';

const errText = (e, fallback) =>
  e?.response?.data?.error || e?.response?.data?.detail || e?.message || fallback;

// The backend clips the note at 120 characters. Stopping the typing at the
// same length keeps the preview below honest — what is on screen is exactly
// what lands on the phones.
const NOTE_LIMIT = 120;

// Mirrors the notice apps/notifications/firebase.py builds, so the superadmin
// proof-reads the real thing before it goes out. Keep the two in step.
const noticeTitle = (version) => (version ? `Update available — version ${version}` : 'Update available');
const noticeBody = (note) =>
  `A newer version of the app is ready on the Play Store. Tap to update.${note ? ` ${note}` : ''}`;

const AppUpdateNotice = () => {
  const [version, setVersion] = useState('');
  const [note, setNote] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  // The minimum build the app is HELD to. A notice can be ignored; this
  // cannot — a phone on an older build gets an update screen it cannot
  // dismiss until it has updated. Loaded from the backend so the form shows
  // what is in force right now, not a blank box.
  const [minVersion, setMinVersion] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState(null);

  useEffect(() => {
    let active = true;
    Promise.resolve(appUpdateApi.getRelease?.())
      .then((res) => {
        if (!active || !res?.data) return;
        setVersion((v) => v || res.data.latest_version || '');
        setNote((n) => n || res.data.note || '');
        setMinVersion(res.data.min_version || '');
      })
      .catch(() => { /* the form still works; it just starts blank */ });
    return () => { active = false; };
  }, []);

  const trimmedVersion = version.trim();
  const trimmedNote = note.trim();
  const trimmedMin = minVersion.trim();

  const saveVersions = async () => {
    setSaving(true);
    setSaveResult(null);
    try {
      await appUpdateApi.saveRelease({
        latest_version: trimmedVersion,
        min_version: trimmedMin,
        note: trimmedNote,
      });
      setSaveResult({
        tone: 'success',
        message: trimmedMin
          ? `Saved. Phones on a build older than ${trimmedMin} are now blocked until they update.`
          : 'Saved. No minimum is set — no phone is blocked.',
      });
    } catch (e) {
      setSaveResult({ tone: 'error', message: errText(e, 'Could not save the versions') });
    } finally {
      setSaving(false);
    }
  };

  const send = async () => {
    setSending(true);
    setError(null);
    try {
      // Both fields are optional — send only what was filled in, so an empty
      // box never reaches the phones as "version " with nothing after it.
      const payload = {};
      if (trimmedVersion) payload.version = trimmedVersion;
      if (trimmedNote) payload.note = trimmedNote;

      const { data } = await appUpdateApi.push(payload);
      const devices = data?.devices ?? 0;

      // Reaching nobody is not a success. The request worked, the supervisors
      // still heard nothing, and a green tick there would send the superadmin
      // away believing the team has been told.
      setResult(
        devices
          ? {
              tone: 'success',
              message: `Update notice sent to ${devices} supervisor device${devices === 1 ? '' : 's'}.`,
            }
          : {
              tone: 'warning',
              message: 'No supervisor device is registered for notifications, so nothing was sent.',
            },
      );
      setConfirming(false);
    } catch (e) {
      setError(errText(e, 'Could not send the update notice'));
    } finally {
      setSending(false);
    }
  };

  return (
    <Card title="Supervisor App" subtitle="Tell every supervisor a new version is on the Play Store">
      <div className="space-y-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 shrink-0 rounded-xl bg-violet-50 flex items-center justify-center">
            <FiSmartphone className="w-5 h-5 text-violet-600" />
          </div>
          <p className="text-xs leading-5 text-slate-500">
            Every supervisor phone with the app installed gets a notification, and tapping it
            opens the Play Store page. Most phones are offered the update by Play itself when
            the app is opened — send this when a release should not wait for that.
          </p>
        </div>

        {result && (
          <Alert type={result.tone} message={result.message} onClose={() => setResult(null)} />
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Version <span className="font-normal text-slate-400">(optional)</span>
            </label>
            <input
              type="text"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              disabled={sending}
              placeholder="e.g. 1.4.0"
              className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-400 transition-all"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              What changed <span className="font-normal text-slate-400">(optional)</span>
            </label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={sending}
              maxLength={NOTE_LIMIT}
              placeholder="e.g. Restock screen loads faster"
              className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-400 transition-all"
            />
          </div>
        </div>

        <div className="rounded-xl border border-amber-100 bg-amber-50/60 p-4">
          <label htmlFor="app-min-version" className="block text-sm font-semibold text-slate-700 mb-1">
            Minimum version required <span className="font-normal text-slate-400">(blocks older builds)</span>
          </label>
          <p className="mb-3 text-xs leading-5 text-slate-500">
            A phone on a build older than this is stopped with an &ldquo;Update required&rdquo;
            screen until it updates from the Play Store. Leave empty to block nobody.
          </p>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <input
              id="app-min-version"
              type="text"
              value={minVersion}
              onChange={(e) => setMinVersion(e.target.value)}
              disabled={saving}
              placeholder="e.g. 1.12"
              className="w-full sm:max-w-xs px-4 py-3 border border-slate-200 rounded-xl text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-amber-400 transition-all"
            />
            <Button variant="secondary" size="md" loading={saving} onClick={saveVersions}>
              Save versions
            </Button>
          </div>
          {saveResult && (
            <div className="mt-3">
              <Alert type={saveResult.tone} message={saveResult.message} onClose={() => setSaveResult(null)} />
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <Button
            variant="primary"
            size="md"
            onClick={() => {
              setError(null);
              setConfirming(true);
            }}
          >
            <FiSend className="w-4 h-4 mr-2" /> Send update notice
          </Button>
        </div>
      </div>

      {/* Confirm — the last stop before every supervisor's phone buzzes */}
      {confirming && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
          onClick={() => { if (!sending) setConfirming(false); }}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="mb-3 flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-500">
                <FiAlertTriangle className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <p className="text-base font-bold text-slate-900">Send this to every supervisor?</p>
                <p className="text-xs text-slate-500">
                  Every supervisor phone is notified at once, and a notice cannot be recalled.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setConfirming(false)}
                disabled={sending}
                className="ml-auto text-slate-400 hover:text-slate-700 disabled:opacity-50"
                aria-label="Close"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>

            <div className="rounded-xl bg-slate-50 px-4 py-3">
              <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-400">
                What they will see
              </p>
              <p className="text-sm font-bold text-slate-800">{noticeTitle(trimmedVersion)}</p>
              <p className="mt-0.5 text-xs leading-5 text-slate-600">{noticeBody(trimmedNote)}</p>
            </div>

            {error && <div className="mt-3"><Alert type="error" message={error} /></div>}

            <div className="mt-4 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setConfirming(false)} disabled={sending}>
                Cancel
              </Button>
              <Button variant="primary" loading={sending} onClick={send}>
                Send notice
              </Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default AppUpdateNotice;
